import { Button } from '@/components/common/button';
import { Select } from '@/components/common/select';

import {
  flowRateAxis,
  flowRateLabel,
  flowRateLabelInterval,
  formatRuntime,
  formatUpTo2Decimals,
} from '../details-rules';
import { FLOW_RATE_RANGES, type FlowRateRange, type FlowRateSeries } from '../details-schemas';

import styles from './flow-rate-card.module.scss';
import { FlowRateChart } from './flow-rate-chart';

const NO_VALUE = '—';

export type FlowRateCardProps = {
  series: FlowRateSeries | undefined;
  range: FlowRateRange;
  isLoading: boolean;
  isError: boolean;
  onRangeChange: (range: FlowRateRange) => void;
  onRetry: () => void;
};

/**
 * The "Flow Rate - Gal/Min" card (US-112437 §3, §4): range selector, prominent average, the line
 * chart, and the Average / Runtime / Gallons Pumped summary for the selected range.
 *
 * Its error state is local by design — §13 requires a section that fails to show its own error
 * while the rest of the page keeps working.
 */
export function FlowRateCard({
  series,
  range,
  isLoading,
  isError,
  onRangeChange,
  onRetry,
}: FlowRateCardProps) {
  const points = series?.points ?? [];
  const axis = flowRateAxis(points);
  const average = formatUpTo2Decimals(series?.averageGpm ?? null);

  // §3 lists the average as part of the chart display itself ("The average flow rate prominently
  // displayed (e.g., '12.6 Avg GPM')"), and the empty state pins it to "0 Avg GPM" rather than a
  // dash. The summary row below repeats it as one of the §4 range totals; this is the headline.
  const headlineAverage = points.length === 0 && !isLoading && !isError ? '0' : average;

  return (
    <section className={styles.card} aria-label="Flow rate">
      <div className={styles.head}>
        <div className={styles.heading}>
          <h2 className={styles.title}>
            Flow Rate <span className={styles.unit}>Gal/Min</span>
          </h2>
          {!isError && !isLoading && headlineAverage ? (
            <p className={styles.average}>
              {headlineAverage}
              <span className={styles.averageUnit}>Avg GPM</span>
            </p>
          ) : null}
        </div>
        <Select
          selectSize="compact"
          className={styles.range}
          value={range}
          options={FLOW_RATE_RANGES.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          onChange={(value) => onRangeChange(value as FlowRateRange)}
          aria-label="Flow rate time range"
        />
      </div>

      {isError ? (
        <div className={styles.state} role="alert">
          <p className={styles.stateText}>Couldn&apos;t load flow rate data, Please try again</p>
          <Button variant="secondary" onClick={onRetry}>
            Retry
          </Button>
        </div>
      ) : isLoading ? (
        <div className={styles.skeleton} aria-busy="true" aria-label="Loading flow rate" />
      ) : points.length === 0 ? (
        <div className={styles.state}>
          <p className={styles.stateText}>No flow rate data for this period</p>
        </div>
      ) : (
        <FlowRateChart
          points={points}
          ticks={axis.ticks}
          max={axis.max}
          labelFor={(point) => flowRateLabel(point, range)}
          labelInterval={flowRateLabelInterval(range, points.length)}
        />
      )}

      <dl className={styles.summary}>
        <div className={styles.summaryItem}>
          <dt className={styles.summaryLabel}>Average</dt>
          <dd className={styles.summaryValue}>
            {/* An empty range still reports "0 Avg GPM" rather than a dash (§3). */}
            {headlineAverage ?? NO_VALUE}
            <span className={styles.summaryUnit}>GPM</span>
          </dd>
        </div>
        <div className={styles.summaryItem}>
          <dt className={styles.summaryLabel}>Runtime</dt>
          <dd className={styles.summaryValue}>
            {formatRuntime(series?.runtimeMinutes ?? null) ?? NO_VALUE}
          </dd>
        </div>
        <div className={styles.summaryItem}>
          <dt className={styles.summaryLabel}>Gallons Pumped</dt>
          <dd className={styles.summaryValue}>
            {formatUpTo2Decimals(series?.gallonsPumped ?? null) ?? NO_VALUE}
            <span className={styles.summaryUnit}>Gal</span>
          </dd>
        </div>
      </dl>
    </section>
  );
}
